import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { OAuthServiceSchema } from "./oauth-start.schema";

// OAuthConnectionStatus enum schema
export const OAuthConnectionStatusSchema = z.nativeEnum(DbEnum.OAuthConnectionStatus);

/**
 * Linked Deployment Info Schema
 *
 * Summary of a deployment linked to this connection.
 * Used in the connection picker to show which deployments are already using the connection.
 */
export const LinkedDeploymentInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  isActive: z.boolean()
});

/**
 * OAuth Connection Response Schema
 *
 * Public-facing connection data WITHOUT sensitive tokens.
 * Used in list responses and embedded in deployment responses.
 *
 * settings is typed as opaque JSON at the transport layer.
 * Use parseOAuthConnectionSettings() from @nexus/types to get typed access.
 */
export const OAuthConnectionResponseSchema = z.object({
  id: z.string().uuid(),
  service: OAuthServiceSchema,
  status: OAuthConnectionStatusSchema,
  isHealthy: z.boolean(),
  needsReauth: z.boolean(),
  failureReason: z.string().nullable(),
  // Account info (for display: "Connected as john@gmail.com")
  accountId: z.string().nullable(),
  accountEmail: z.string().nullable(),
  accountName: z.string().nullable(),
  accountAvatar: z.string().nullable(),
  /** Service-specific settings — parse with parseOAuthConnectionSettings() for typed access */
  settings: z.record(z.string(), z.unknown()).nullable(),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime().nullable(),
  lastRefreshedAt: z.string().datetime().nullable(),
  /** Deployments linked to this connection (for picker UI to show in-use state) */
  linkedDeployments: z.array(LinkedDeploymentInfoSchema).optional()
});

export type OAuthConnectionResponse = z.infer<typeof OAuthConnectionResponseSchema>;

/**
 * List Connections Response
 *
 * Returns all deployment connections for authenticated user's organization.
 * Organization ID extracted from Clerk JWT by backend.
 */
export const ListConnectionsResponseSchema = z.object({
  connections: z.array(
    OAuthConnectionResponseSchema.extend({
      organizationId: z.string()
    })
  )
});
